type EventType = { [type: string]: { title: string; description: string } };
type AllEventsType = { [type: string]: { title: string; description: string; features: EventType } };

const workpaperEvents: EventType = {
    WORKPAPER_ASSIGNED_TO: {
        title: 'Assigned to a workpaper',
        description: 'Get notified when you are are assigned to a workpaper.',
    },
    WORKPAPER_UNASSIGNED_FROM: {
        title: 'Removed from a workpaper',
        description: 'Get notified when you are removed from a workpaper.',
    },
    WORKPAPER_ASSIGNEES_ADDED: {
        title: 'Assignees added',
        description: 'Get notified when other users are added to a workpaper you are assigned to.',
    },
    WORKPAPER_ASSIGNEES_REMOVED: {
        title: 'Assignees removed',
        description: 'Get notified when other users are removed from a workpaper you are assigned to.',
    },
    WORKPAPER_GUESTS_ADDED: {
        title: 'Guests added',
        description: 'Get notified when guests are added to a workpaper you are assigned to.',
    },
    WORKPAPER_GUESTS_REMOVED: {
        title: 'Guests removed',
        description: 'Get notified when guests are removed from a workpaper you are assigned to.',
    },
    WORKPAPER_ACTIVE: {
        title: 'Workpaper reopened',
        description: 'Get notified when a workpaper you are assigned to reopens.',
    },
    WORKPAPER_ARCHIVED: {
        title: 'Workpaper is archived',
        description: 'Get notified when a workpaper you are assigned to is archived.',
    },
    WORKPAPER_COMPLETED: {
        title: 'Workpaper is completed',
        description: 'Get notified when a workpaper you are assigned to is completed.',
    },
    WORKPAPER_EXPORT_COMPLETED: {
        title: 'Export completed',
        description: 'Get notified when a scheduled workpaper export is completed.',
    },
    WORKPAPER_EXPORT_ERROR: {
        title: 'Export error',
        description: 'Get notified when a scheduled workpaper export errors.',
    },
};

const worksheetEvents: EventType = {
    WORKSHEET_ASSIGNED_TO: {
        title: 'Assigned to a worksheet',
        description: 'Get notified when you are are assigned to a worksheet.',
    },
    WORKSHEET_UNASSIGNED_FROM: {
        title: 'Removed from a worksheet',
        description: 'Get notified when you are removed from a worksheet.',
    },
    WORKSHEET_ASSIGNEES_ADDED: {
        title: 'Assignees added',
        description: 'Get notified when other users are added to a worksheet you are assigned to.',
    },
    WORKSHEET_ASSIGNEES_REMOVED: {
        title: 'Assignees removed',
        description: 'Get notified when other users are removed from a worksheet you are assigned to.',
    },
    WORKSHEET_REVIEWED: {
        title: 'Worksheet is reviewed',
        description: 'Get notified when a worksheet you are assigned to is reviewed.',
    },
    WORKSHEET_AWAITING_REVIEW: {
        title: 'Worksheet is awaiting review',
        description: 'Get notified when a worksheet you are assigned to is awaiting review.',
    },
    WORKSHEET_EXCLUDED: {
        title: 'Worksheet is excluded',
        description: 'Get notified when a worksheet you are assigned to is excluded.',
    },
    WORKSHEET_UNPREPARED: {
        title: 'Worksheet is unprepared',
        description: 'Get notified when a worksheet you are assigned to is unprepared.',
    },
    WORKSHEET_DELETED: {
        title: 'Worksheet is deleted',
        description: 'Get notified when a worksheet you are assigned to is deleted.',
    },
};

const certificationEvents: EventType = {
    CERTIFICATION_READY: {
        title: 'Certification is ready',
        description: 'Get notified when a workpaper you are assigned to is ready for certification.',
    },
    CERTIFICATION_DONE: {
        title: 'Users have certified',
        description: 'Get notified when other users certify on a workpaper you are assigned to.',
    },
    CERTIFICATION_REMOVED: {
        title: 'Certification removed',
        description: 'Get notified when your certification status on a workpaper has been removed.',
    },
    CERTIFICATION_STATUS_CHANGE: {
        title: 'Certification status has changed',
        description: 'Get notified when certification by other users on a workpaper you are assigned to is removed.',
    },
};

const commentEvents: EventType = {
    COMMENT_TAGGED: {
        title: 'Tagged in an internal comment',
        description: 'Get notified when you are tagged in an internal comment.',
    },
    COMMENT_REPLIED: {
        title: 'Replies to your internal comment',
        description: 'Get notified when users reply to your internal comment.',
    },
    COMMENT_ACTIVE: {
        title: 'Internal comment is active',
        description: 'Get notified when your internal comment becomes active.',
    },
    COMMENT_RESOLVED: {
        title: 'Internal comment is resolved',
        description: 'Get notified when your internal comment is resolved.',
    },
};

const queryEvents: EventType = {
    QUERY_TAGGED: {
        title: 'Tagged in a client query',
        description: 'Get notified when you are tagged in a client query.',
    },
    QUERY_REPLIED: {
        title: 'Replies to your client query',
        description: 'Get notified when users reply to your client query.',
    },
    QUERY_ACTIVE: {
        title: 'Client query is active',
        description: 'Get notified when your client query becomes active.',
    },
    QUERY_RESOLVED: {
        title: 'Client query is resolved',
        description: 'Get notified when your client query is resolved.',
    },
};

export const allNotificationEvents: AllEventsType = {
    workpaper: {
        title: 'Workpaper events',
        description:
            'Configure the notifications you receive within the app for events related to a workpaper, such as assigning, workpaper status changes, and exporting.',
        features: workpaperEvents,
    },
    worksheet: {
        title: 'Worksheet events',
        description:
            'Configure the notifications you receive within the app for events related to worksheets, such as assigning and worksheet status changes.',
        features: worksheetEvents,
    },
    certification: {
        title: 'Workpaper certification events',
        description:
            'Configure the notifications you receive within the app for events related to workpaper certifications, such as certifications completed, yours and other’s certification status changes.',
        features: certificationEvents,
    },
    comment: {
        title: 'Internal comments events',
        description:
            'Configure the notifications you receive within the app for events related to internal comments, such as tagging, replies, and status changes.',
        features: commentEvents,
    },
    query: {
        title: 'Client query events',
        description:
            'Configure the notifications you receive within the app for events related to client queries, such as tagging, replies, and status changes.',
        features: queryEvents,
    },
};
